import { useState } from "react";
import { format } from "date-fns";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HelpHint } from "@/components/HelpHint";
import { InfoTip } from "@/components/InfoTip";
import { CalendarClock, CheckCircle2, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";
import { LegalDisclaimer } from "@/context/DisclaimerContext";

// Deposit-return deadlines vary by state/country — the app never supplies or calculates one.
// The tenant enters the deadline date themselves; this component only counts down to it.
const DEADLINE_HELPER =
  "Deposit return deadlines vary by state/country — check your lease or your state's official " +
  "housing resources, then enter the date here.";

function tone(days) {
  if (days == null) return "gray";
  if (days < 3) return "red";
  if (days <= 14) return "yellow";
  return "green";
}
const TONE_TEXT = { green: "text-[#177245]", yellow: "text-[#92400E]", red: "text-[#B91C1C]", gray: "text-[#64748B]" };
const TONE_CARD = {
  green: "border-[#A7F3D0] bg-[#ECFDF5]",
  yellow: "border-[#FDE68A] bg-[#FFFBEB]",
  red: "border-[#FECACA] bg-[#FEF2F2]",
  gray: "border-[#E2E8F0] bg-white",
};
const fmtDate = (d) => { try { return format(new Date(d), "MMM d, yyyy"); } catch { return d; } };

/**
 * DeadlineCountdown — one component, two variants.
 * variant="full": Property Detail card (setup prompt / resolved / countdown + disclaimer + mark-returned).
 * variant="compact": Dashboard tile (address + colored days number, tap-through).
 */
export function DeadlineCountdown({ variant = "full", status, onSetup, onMarkReturned, marking, onEscalate }) {
  const s = status || {};
  const configured = s.configured;
  const returned = s.deposit_returned;
  const days = s.days_remaining;
  const t = tone(days);

  // ---------- COMPACT (dashboard) ----------
  if (variant === "compact") {
    if (!configured || returned) return null;
    const overdue = days < 0;
    return (
      <div data-testid="deadline-compact" className={`mt-3 rounded-xl border px-3 py-2 flex items-center justify-between gap-2 ${TONE_CARD[t]}`}>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">Deposit deadline</p>
          <p className={`text-sm font-bold ${TONE_TEXT[t]}`}>
            {overdue ? `${Math.abs(days)} days overdue` : `${days} days left`}
          </p>
        </div>
        <ArrowRight size={15} className="text-[#CBD5E1] shrink-0" />
      </div>
    );
  }

  // ---------- FULL (property detail) ----------
  // Setup prompt
  if (!configured) {
    return (
      <div data-testid="deadline-setup-prompt" className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-5">
        <div className="flex items-start gap-3">
          <CalendarClock size={20} className="text-brand mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-navy flex items-center gap-1.5">Track your deposit-return deadline <InfoTip topic="depositDeadline" /></p>
            <p className="mt-1 text-sm text-[#475569]">{DEADLINE_HELPER}</p>
            <Button data-testid="deadline-setup-btn" onClick={onSetup} variant="outline" className="mt-3 rounded-xl">Enter deadline date</Button>
          </div>
        </div>
      </div>
    );
  }

  // Resolved
  if (returned) {
    return (
      <div data-testid="deadline-returned" className="rounded-2xl border border-[#A7F3D0] bg-[#ECFDF5] p-5">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={22} className="text-[#177245]" />
          <div>
            <p className="font-heading font-semibold text-[#177245]">Deposit returned</p>
            {s.deposit_returned_date && <p className="text-sm text-[#334155]">Marked returned on {fmtDate(s.deposit_returned_date)}.</p>}
          </div>
        </div>
      </div>
    );
  }

  // Countdown
  const overdue = days < 0;
  return (
    <div data-testid="deadline-countdown-card" className={`rounded-2xl border p-5 ${overdue ? "border-[#E2E8F0] bg-white" : TONE_CARD[t]}`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#64748B] flex items-center gap-1.5">Deposit return deadline <InfoTip topic="depositDeadline" /></p>
          {overdue ? (
            <p className="mt-1 font-heading text-lg font-semibold text-[#334155]" data-testid="deadline-days">Deposit-return date you recorded: {s.deadline_date ? fmtDate(s.deadline_date) : "set"} · passed</p>
          ) : (
            <p className={`mt-1 font-heading ${TONE_TEXT[t]}`} data-testid="deadline-days">
              <span className="text-4xl font-bold">{days}</span> <span className="text-lg font-semibold">days remaining</span>
            </p>
          )}
          <p className="mt-1 text-sm text-[#475569]">{overdue ? "This is the date you entered. TenantSeal doesn’t determine legal deadlines." : "Based on the deadline date you entered."}</p>
          {!overdue && s.deadline_date && <p className="text-xs text-[#94A3B8]">Deadline date: {fmtDate(s.deadline_date)}</p>}
        </div>
        {overdue ? <CalendarClock size={26} className="text-[#94A3B8] shrink-0" /> : <CalendarClock size={26} className={`${TONE_TEXT[t]} shrink-0`} />}
        <HelpHint topic="deadline" className="shrink-0 -mt-1" />
      </div>

      <MarkReturnedDialog onMarkReturned={onMarkReturned} marking={marking} />

      <div className="mt-3" data-testid="deadline-disclaimer"><LegalDisclaimer keyName="deadline_tracker" className="text-[11px] leading-relaxed text-[#94A3B8]" /></div>
    </div>
  );
}

function MarkReturnedDialog({ onMarkReturned, marking }) {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button data-testid="mark-returned-btn" variant="outline" className="mt-4 rounded-xl border-[#E2E8F0]">
          <CheckCircle2 size={16} className="mr-2" /> Mark deposit returned
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark deposit as returned?</AlertDialogTitle>
          <AlertDialogDescription>This stops future deadline reminders for this property.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-1">
          <Label className="text-sm">Date returned</Label>
          <Input data-testid="returned-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-mark-returned" disabled={marking} onClick={() => onMarkReturned(date)} className="bg-[#177245] hover:bg-[#054C39]">
            {marking ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
